import is from '@sindresorhus/is';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';

import { useInvalidateDocuments } from '../../../../fetchers/documents/api/use-invalidate-documents';
import { useGetUploadUrls } from '../../../api/use-documents';

export type DocumentUploadEvent = {
    readonly id: string;
    readonly name: string;
    readonly status: string;
};

type DocumentUploadNotification = {
    readonly payload: {
        readonly document?: DocumentUploadEvent;
        readonly type: string;
    };
};

/** Every status change of a knowledge-base document in this tenant, as the ingestion pipeline reports it. */
export const useDocumentUploadEvents = (
    onDocument: (document: DocumentUploadEvent) => void,
    { enabled = true }: { readonly enabled?: boolean } = {},
): void => {
    const { realtimeEventListenerBuilder } = useApplicationContext();
    const invalidateDocuments = useInvalidateDocuments();

    useEffect(() => {
        if (!enabled) return;

        const listener = realtimeEventListenerBuilder.build({
            method: 'GET',
            path: '/v1/events/tenant',
        });

        listener.addEventListener('notification', (event: MessageEvent) => {
            const { payload } = JSON.parse(event.data as string) as DocumentUploadNotification;

            if (payload.type === 'DOCUMENT_UPLOAD' && !is.undefined(payload.document)) {
                invalidateDocuments();
                onDocument(payload.document);
            }
        });

        listener.start();

        return () => {
            listener.stop();
        };
    }, [enabled, invalidateDocuments, onDocument, realtimeEventListenerBuilder]);
};

/**
 * Uploads files straight to the knowledge-base bucket through presigned URLs, reporting failures as
 * snackbars. Resolves with the names of the files that did not upload, including any the server
 * returned no upload URL for.
 */
export const useUploadDocumentFiles = (): {
    readonly uploadFiles: (files: ReadonlyMap<string, File>) => Promise<readonly string[]>;
} => {
    const translate = useTranslate();
    const { enqueueSnackbar } = useSnackbar();
    const { getUploadUrls } = useGetUploadUrls();
    const invalidateDocuments = useInvalidateDocuments();

    const uploadFiles = useCallback(
        async (files: ReadonlyMap<string, File>): Promise<readonly string[]> => {
            const presignedUrls = await getUploadUrls([...files.keys()]).catch(() => null);

            if (is.null(presignedUrls)) {
                enqueueSnackbar(translate('pages.job-architecture-v2.knowledge-base.failed-to-obtain-upload-urls'), {
                    variant: 'error',
                });
                return [...files.keys()];
            }

            const failedNames = await Promise.all(
                [...files.entries()].map(async ([name, file]) => {
                    const presignedUrl: string | undefined = presignedUrls[name];

                    try {
                        if (is.undefined(presignedUrl)) {
                            throw new Error('Missing upload URL');
                        }
                        const response = await fetch(presignedUrl, { body: file, method: 'PUT' });
                        if (!response.ok) {
                            throw new Error(`Upload failed with status ${response.status}`);
                        }
                        return null;
                    } catch {
                        enqueueSnackbar(
                            translate('pages.job-architecture-v2.knowledge-base.failed-to-upload-file', { name }),
                            { variant: 'error' },
                        );
                        return name;
                    }
                }),
            );

            const failures = failedNames.filter(is.string);
            if (failures.length < files.size) invalidateDocuments();
            return failures;
        },
        [enqueueSnackbar, getUploadUrls, invalidateDocuments, translate],
    );

    return { uploadFiles };
};
